/**
 * カート（05 §6.2 A-10）。
 *
 * ★**内部は卸ごとに独立**（S12 仕様・M1.6 でも不変）＝みどり建材のカートに入れた合板が、
 *   サンプル建材のカートに現れてはいけない。tenant_id をキーにして分ける。
 *   **M1.6（D-83）で変わったのは見せ方だけ**＝画面は全卸ぶんを1つのカートとして並べ、
 *   確定は `POST /partner/checkout` 1回（卸ごとの子注文にサーバが分ける）。
 *   グループ構造がそのまま「卸ごとのグループカード」になる。
 *
 * ★**localStorage に置かない**＝メモリ保持。理由はトークンと同じではなく、
 *   「取引単価は卸ごと・時期ごとに変わる」から＝古いカートを復元すると、
 *   画面に出ている値段と実際に注文される値段がズレる余地ができる。
 *   カートはその場の作業＝リロードで消えてよい（デモ v3 の挙動とも一致）。
 *
 * ★**金額の計算をここに置かない**（M1.6）＝小計・配送料・合計は `pages/Cart.tsx` の
 *   `useCartGroups` が唯一の実装（配送料の規則はサーバの `resolve_delivery_fee` と対）。
 *   ここにも合計を持たせると、画面ごとに違う金額が出る3つ目の実装ができる。
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export type CartLine = {
  product_id: number;
  name: string;
  spec: string | null;
  unit: string;
  unit_price: string | null;
  qty: number;
};

type CartState = {
  lines: (tenantId: number) => CartLine[];
  add: (tenantId: number, line: CartLine) => void;
  setQty: (tenantId: number, productId: number, qty: number) => void;
  remove: (tenantId: number, productId: number) => void;
  clear: (tenantId: number) => void;
  /** 全卸を合算した**品目数**（下部タブのバッジ＝D-83「バッジは全卸合算」）。 */
  countAll: () => number;
  /** 確定できた卸のぶんだけ空にする（送れなかった卸の品を消さないため＝A-06）。 */
  clearMany: (tenantIds: number[]) => void;
};

const Ctx = createContext<CartState | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [byTenant, setByTenant] = useState<Record<number, CartLine[]>>({});

  const lines = useCallback((t: number) => byTenant[t] ?? [], [byTenant]);

  const add = useCallback((t: number, line: CartLine) => {
    setByTenant((prev) => {
      const cur = prev[t] ?? [];
      const at = cur.findIndex((l) => l.product_id === line.product_id);
      // 同じ商品を2回入れたら**足す**（別行にしない）＝現場の感覚と一致
      const next = at >= 0 ? cur.map((l, i) => (i === at ? { ...l, qty: l.qty + line.qty } : l)) : [...cur, line];
      return { ...prev, [t]: next };
    });
  }, []);

  const setQty = useCallback((t: number, productId: number, qty: number) => {
    setByTenant((prev) => ({
      ...prev,
      [t]: (prev[t] ?? []).map((l) => (l.product_id === productId ? { ...l, qty } : l)),
    }));
  }, []);

  const remove = useCallback((t: number, productId: number) => {
    setByTenant((prev) => ({ ...prev, [t]: (prev[t] ?? []).filter((l) => l.product_id !== productId) }));
  }, []);

  const clear = useCallback((t: number) => setByTenant((prev) => ({ ...prev, [t]: [] })), []);

  // ★**一括チェックアウトで実際に送れた卸だけ**を空にする（D-83）。全部消すと、停止中の卸や
  //   まだ送っていない卸の品まで黙って消える＝「入れたはずのものが無い」になる。
  const clearMany = useCallback(
    (tenantIds: number[]) =>
      setByTenant((prev) => {
        const next = { ...prev };
        for (const t of tenantIds) next[t] = [];
        return next;
      }),
    [],
  );

  const value = useMemo<CartState>(
    () => ({
      lines,
      add,
      setQty,
      remove,
      clear,
      clearMany,
      // ★**品目数**（数量の合計ではない）。タブのバッジは「いま何品入っているか」と読まれる。
      //   数量合計にすると、石膏ボードを60枚入れただけで「60」、2品で「120」と出て、
      //   3桁がバッジからはみ出すうえ意味も伝わらない（枚数は各行に出ている）。
      countAll: () => Object.values(byTenant).reduce((n, ls) => n + ls.length, 0),
    }),
    [byTenant, lines, add, setQty, remove, clear, clearMany],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart(): CartState {
  const v = useContext(Ctx);
  if (!v) throw new Error("CartProvider の外で useCart が呼ばれた");
  return v;
}
